import { Box, Divider, Grid } from '@material-ui/core';
import { FeePaymentType, FeeType } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { PricingSpecSearchSwitch } from '@/components/modules/PricingEditor/components/PricingSpecSearchSwitch';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { Font } from '@/components/shared/components/Font';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

export const PricingSpecDataFormFields = () => {
  const [moreOptions, setMoreOptions] = useState(false);
  const [pricingSpecSearchEnabled, setPricingSpecSearchEnabled] = useState(true);

  const watcher = useFormContext()?.watch;

  const merchantFeeTypeWatcher = watcher('merchantFeeType');
  const setupFeeTypeWatcher = watcher('setupFeeType');

  return (
    <>
      <main style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <section
          style={{
            width: '100%',
            margin: '32px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gridGap: 8,
          }}
        >
          <>
            <Grid container spacing={6}>
              <Grid item xs={12} sm={6}>
                <FormSectionTitle tId={'PRINCIPAL'} />
                <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                  <NumberFormField autoFocus name={'minPrincipal'} label={'MINIMUM_AMOUNT'} valueUnit={'€'} />
                  <NumberFormField name={'maxPrincipal'} label={'MAXIMUM_AMOUNT'} valueUnit={'€'} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSectionTitle tId={'TERM'} />
                <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                  <NumberFormField name={'minTerm'} label={'minTerm'} type={'number'} allowDecimals={false} />
                  <NumberFormField name={'maxTerm'} label={'maxTerm'} type={'number'} allowDecimals={false} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSectionTitle tId={'ANNUAL_INTEREST_AND_DISCOUNT'} />
                <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                  <NumberFormField name={'annualInterest'} label={'annualInterest'} valueUnit={'%'} />
                  <NumberFormField
                    name={'merchantDiscountPercentage'}
                    label={'merchantDiscountPercentage'}
                    valueUnit={'%'}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSectionTitle tId={'SETUP_FEE_COST'} />
                <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                  <SelectFormField
                    name={'setupFeePaymentType'}
                    label="setupFeePaymentType"
                    selectOptions={Object.values(FeePaymentType).map((feePaymentType) => ({
                      value: feePaymentType,
                      display: <Text tId={`FEE_PAYMENT_TYPE_${feePaymentType}`} />,
                    }))}
                  />
                  <SelectFormField
                    name={'setupFeeType'}
                    label="setupFeeType"
                    selectOptions={Object.values(FeeType).map((feeType) => ({
                      value: feeType,
                      display: <Text tId={`FEE_TYPE_${feeType}`} />,
                    }))}
                  />
                  <NumberFormField
                    name={'setupFeeValue'}
                    label={'setupFeeValue'}
                    valueUnit={setupFeeTypeWatcher === FeeType.FIX_AMOUNT ? '€' : '%'}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box my={4} display={'flex'} alignItems={'center'} width={'100%'} gridGap={8}>
              <Divider light style={{ flexGrow: 1 }} />
              <StandardButton size="small" onClick={() => setMoreOptions(!moreOptions)}>
                <Text tId={moreOptions ? 'LESS_OPTIONS' : 'MORE_OPTIONS'} />
              </StandardButton>
              <Divider light style={{ flexGrow: 1 }} />
            </Box>
            {moreOptions && (
              <Grid container spacing={4} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6}>
                  <FormSectionTitle tId={'GRACE_PERIOD'} />
                  <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                    <NumberFormField name={'gracePeriod'} label={'gracePeriod'} type={'number'} allowDecimals={false} />
                    <NumberFormField name={'gracePercentage'} label={'gracePercentage'} valueUnit={'%'} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormSectionTitle tId={'MERCHANT_FEE_COMMISSION'} />
                  <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                    <SelectFormField
                      name={'merchantFeeType'}
                      label="merchantFeeType"
                      selectOptions={Object.values(FeeType).map((feeType) => ({
                        value: feeType,
                        display: <Text tId={`FEE_TYPE_${feeType}`} />,
                      }))}
                    />
                    <NumberFormField
                      name={'merchantFeeValue'}
                      label={'merchantFeeValue'}
                      valueUnit={merchantFeeTypeWatcher === FeeType.FIX_AMOUNT ? '€' : '%'}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormSectionTitle tId={'REGISTRATION'} />
                  <Box display={'flex'} flexDirection={'column'} gridGap={'16px'} pt={1}>
                    <NumberFormField
                      name={'yearsSinceAutoRegistrationMin'}
                      label={'yearsSinceAutoRegistrationMin'}
                      type={'text'}
                      allowDecimals={false}
                    />
                    <NumberFormField
                      name={'yearsSinceAutoRegistrationMax'}
                      label={'yearsSinceAutoRegistrationMax'}
                      type={'text'}
                      allowDecimals={false}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
            <PricingSpecSearchSwitch
              pricingSpecSearchEnabled={pricingSpecSearchEnabled}
              setPricingSpecSearchEnabled={setPricingSpecSearchEnabled}
            />
          </>
        </section>
      </main>
    </>
  );
};

const FormSectionTitle = ({ tId }: { tId: string }) => {
  return (
    <Box>
      <Font variant={'body2'}>
        <Text tId={tId} strong />
      </Font>
      <Box mt={1} />
    </Box>
  );
};
